import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

interface CampaignContext {
  campaign: {
    id: string;
    orgId: string;
    title: string;
    subject: string;
    status: string;
    templateId: string;
    senderProfileId: string;
    segmentIds: string[];
    tagIds: string[];
  };
  template: {
    id: string;
    name: string;
    category: string | null;
    thumbnailUrl: string | null;
  };
  senderProfiles: Array<{
    id: string;
    name: string;
    email: string;
    verified: boolean;
  }>;
  segments: Array<{
    id: string;
    name: string;
  }>;
  tags: string[];
  performedBy: string;
}

interface ConfirmSendProps {
  campaignTitle: string;
  receiver: string;
  tags: string;
  subject: string;
  senderName: string;
  senderEmail: string;
  selectedTemplateImg: string;
  handleBack: () => void;
  campaignId?: string;
  template?: any;
  selectionType: string;
  selectedContacts: string[];
}

// ⭐ Timezone mapping - Display name to API value
const timezoneMap: Record<string, string> = {
  '(UTC+05:30) Kolkata': 'Asia/Kolkata',
  '(UTC+00:00) London': 'Europe/London',
  '(UTC-05:00) New York': 'America/New_York',
  '(UTC+01:00) Paris': 'Europe/Paris',
  '(UTC+02:00) Dubai': 'Asia/Dubai',
  '(UTC+09:00) Tokyo': 'Asia/Tokyo',
  '(UTC-08:00) Los Angeles': 'America/Los_Angeles',
};

// Final step of the campaign creation process
const ConfirmSend: React.FC<ConfirmSendProps> = ({
  campaignTitle,
  receiver,
  tags,
  subject,
  senderName,
  senderEmail,
  selectedTemplateImg,
  handleBack,
  campaignId,
  template,
  selectionType,
  selectedContacts,
}) => {
  const navigate = useNavigate();

  const [timezone, setTimezone] = useState('');
  const [sendOption, setSendOption] = useState<'now' | 'schedule'>('now');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // ⭐ State for context data
  const [contextData, setContextData] = useState<CampaignContext | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);

  // ⭐ Fetch context data when component mounts
  useEffect(() => {
    const fetchContext = async () => {
      if (!campaignId) {
        return;
      }

      try {
        setLoadingContext(true);
        const { token, tenantId, userId } = getAuthContext();

        if (!token || !tenantId) {
          toast.error('Authentication required');
          setLoadingContext(false);
          return;
        }

        const response = await fetch(
          `${BASE_URL}/v1/campaign-wizard/${campaignId}/context?`,
          {
            method: 'GET',
            headers: {
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setContextData(data);
      } catch (error: any) {
        // console.error('❌ Failed to fetch campaign context:', error);
        toast.error(error.message || 'Failed to load campaign details');
      } finally {
        setLoadingContext(false);
      }
    };

    fetchContext();
  }, [campaignId]);

  // Check if form is valid
  const isFormValid = () => {
    // Timezone is always required
    if (!timezone) return false;

    if (sendOption === 'now') {
      // For "Send now", only timezone is required
      return true;
    } else {
      // For "Schedule", timezone, date, and time are required
      return !!(timezone && selectedDate && selectedTime);
    }
  };

  // ⭐ Build selection payload based on selection type
  const buildSelectionPayload = () => {
    if (selectionType === 'tags') {
      return { selectionType: 'TAG' };
    } else if (selectionType === 'segments') {
      return { selectionType: 'SEGMENT' };
    } else if (selectionType === 'contactIds') {
      return {
        selectionType: 'CONTACT_IDS',
        contactIds: selectedContacts || [],
      };
    } else if (selectionType === 'all') {
      return { selectionType: 'ALL' };
    }
    // Default fallback
    return { selectionType: 'ALL' };
  };

  // ⭐ Handle Send Now - calls /send-now API with timezone
  const handleSendNow = async () => {
    if (!campaignId) {
      toast.error('Campaign ID not found');
      return;
    }

    if (!timezone) {
      toast.error('Please select a timezone');
      return;
    }

    setIsProcessing(true);

    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setIsProcessing(false);
        return;
      }

      // Build the payload with selection type and timezone
      const selectionPayload = buildSelectionPayload();
      const payload = {
        timezone: timezoneMap[timezone] || 'Asia/Kolkata',
        ...selectionPayload,
      };


      const response = await fetch(
        `${BASE_URL}/v1/campaign-wizard/${campaignId}/send-now`,
        {
          method: 'POST',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        let errorMessage = `Failed to send campaign: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      toast.success('Campaign sent successfully!');
      setTimeout(() => {
        navigate('/campaigns', { state: { refreshCampaigns: true } });
      }, 1500);

    } catch (error: any) {
      // console.error('❌ Failed to send campaign:', error);
      toast.error(error.message || 'Failed to send campaign');
    } finally {
      setIsProcessing(false);
    }
  };

  // ⭐ Handle Schedule - calls /schedule API with timezone
  const handleSchedule = async () => {
    if (!campaignId) {
      toast.error('Campaign ID not found');
      return;
    }

    if (!timezone) {
      toast.error('Please select a timezone');
      return;
    }

    if (!selectedDate || !selectedTime) {
      toast.error('Please select both date and time');
      return;
    }

    setIsProcessing(true);

    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setIsProcessing(false);
        return;
      }

      const dateTimeString = `${selectedDate} ${selectedTime}:00`;

      // Build the payload with selection type and schedule info
      const selectionPayload = buildSelectionPayload();
      const payload = {
        timezone: timezoneMap[timezone] || 'Asia/Kolkata',
        at: dateTimeString,
        ...selectionPayload,
      };


      const response = await fetch(
        `${BASE_URL}/v1/campaign-wizard/${campaignId}/schedule`,
        {
          method: 'POST',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        let errorMessage = `Failed to schedule campaign: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      toast.success('Campaign scheduled successfully!');
      setTimeout(() => {
        navigate('/campaigns', { state: { refreshCampaigns: true } });
      }, 1500);

    } catch (error: any) {
      // console.error('❌ Failed to schedule campaign:', error);
      toast.error(error.message || 'Failed to schedule campaign');
    } finally {
      setIsProcessing(false);
    }
  };

  // ⭐ Handle button click - decides which API to call
  const handleAction = () => {
    if (sendOption === 'now') {
      handleSendNow();
    } else {
      handleSchedule();
    }
  };

  // ⭐ Get campaign title from context
  const getCampaignTitle = () => {
    return contextData?.campaign?.title || campaignTitle || 'N/A';
  };

  // ⭐ Get subject from context
  const getSubject = () => {
    return contextData?.campaign?.subject || subject || 'N/A';
  };

  // ⭐ Get segment names from context data
  const getSegmentNames = () => {
    if (!contextData) return receiver || 'N/A';

    const segmentIds = contextData.campaign?.segmentIds || [];
    if (segmentIds.length === 0) return 'N/A';

    const segments = contextData.segments || [];
    const names = segmentIds.map(id => {
      const found = segments.find(s => s.id === id);
      return found?.name || id;
    });
    return names.join(', ');
  };

  // ⭐ Get tag names from context data
  const getTagNames = () => {
    if (!contextData) return tags || 'N/A';

    const tagIds = contextData.campaign?.tagIds || [];
    if (tagIds.length === 0) return 'N/A';

    return tagIds.join(', ');
  };

  // ⭐ Get status from context
  const getStatus = () => {
    return contextData?.campaign?.status || 'DRAFT';
  };

  // ⭐ Get template name from context
  const getTemplateName = () => {
    return contextData?.template?.name || 'Untitled Template';
  };

  // ⭐ Get template thumbnail from context
  const getTemplateThumbnail = () => {
    return contextData?.template?.thumbnailUrl || null;
  };

  // ⭐ Get template description - generic hardcoded description
  const getTemplateDescription = () => {
    const templateName = getTemplateName();
    if (templateName && templateName !== 'Untitled Template') {
      return `This is a professionally designed email template for "${templateName}". It features a clean and modern layout optimized for engagement, with clear call-to-action sections and responsive design for all devices.`;
    }
    return 'This is a professionally designed email template with a clean and modern layout optimized for engagement. It features clear call-to-action sections and responsive design for all devices.';
  };

  return (
    <div className='flex gap-6'>
      {/* Left Column: Context */}
      <div className='w-1/3 border rounded-lg p-4 bg-[#F5F8FA] shadow-sm space-y-4 h-[622px] overflow-y-auto'>
        <h4
          className='font-semibold text-[#3F5265] p-2 text-left'
          style={{
            fontSize: '18px',
          }}
        >
          Context
        </h4>

        {loadingContext ? (
          <div className='flex justify-center items-center h-32'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500'></div>
          </div>
        ) : (
          <>
            <div className='p-2'>
              <strong
                className='text-[#9FA9B2] font-normal'
                style={{ fontSize: '12px' }}
              >
                CAMPAIGN TITLE
              </strong>
              <p className='text-[#3F5265]' style={{ fontSize: '14px' }}>
                {getCampaignTitle()}
              </p>
            </div>
            <div className='p-2'>
              <strong
                className='text-[#9FA9B2] font-normal'
                style={{ fontSize: '12px' }}
              >
                SEGMENTS
              </strong>
              <p className='text-[#3F5265]' style={{ fontSize: '14px' }}>
                {getSegmentNames()}
              </p>
            </div>
            <div className='p-2'>
              <strong
                className='text-[#9FA9B2] font-normal'
                style={{ fontSize: '12px' }}
              >
                TAGS
              </strong>
              <p className='text-[#3F5265]' style={{ fontSize: '14px' }}>
                {getTagNames()}
              </p>
            </div>
            <div className='p-2'>
              <strong
                className='text-[#9FA9B2] font-normal'
                style={{ fontSize: '12px' }}
              >
                SUBJECT
              </strong>
              <p className='text-[#3F5265]' style={{ fontSize: '14px' }}>
                {getSubject()}
              </p>
            </div>
            <div className='p-2'>
              <strong
                className='text-[#9FA9B2] font-normal'
                style={{ fontSize: '12px' }}
              >
                STATUS
              </strong>
              <p className='text-[#3F5265]' style={{ fontSize: '14px' }}>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatus() === 'DRAFT' ? 'bg-yellow-100 text-yellow-600' :
                  getStatus() === 'SCHEDULED' ? 'bg-blue-100 text-blue-600' :
                    getStatus() === 'SENT' ? 'bg-green-100 text-green-600' :
                      getStatus() === 'COMPLETED' ? 'bg-gray-100 text-gray-600' :
                        'bg-gray-100 text-gray-600'
                  }`}>
                  {getStatus()}
                </span>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Middle Column: Template */}
      <div className='w-1/3 border rounded-lg p-4 bg-[#F5F8FA] shadow-sm text-center'>
        <h4
          className='font-semibold text-[#3F5265] mb-6 p-2 text-left'
          style={{ fontSize: '18px' }}
        >
          Template
        </h4>

        {loadingContext ? (
          <div className='flex justify-center items-center h-64'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500'></div>
          </div>
        ) : (
          <>
            {/* ⭐ Template Preview - from context API */}
            <div className='w-full h-[180px] rounded-md mb-6 bg-gray-100 flex items-center justify-center overflow-hidden'>
              {getTemplateThumbnail() ? (
                <img
                  src={getTemplateThumbnail()!}
                  alt={getTemplateName()}
                  className='w-full h-full object-cover'
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      const placeholder = parent.querySelector('.template-placeholder-text');
                      if (placeholder) {
                        (placeholder as HTMLElement).style.display = 'flex';
                      }
                    }
                  }}
                />
              ) : (
                <div className='template-placeholder-text w-full h-full flex items-center justify-center bg-gray-200'>
                  <span className='text-gray-400 text-sm'>No thumbnail available</span>
                </div>
              )}
              <div
                className='template-placeholder-text w-full h-full hidden items-center justify-center bg-gray-100'
              >
                <span className='text-gray-400 text-sm'>No thumbnail available</span>
              </div>
            </div>

            {/* ⭐ Template Name - from context API */}
            <h5
              className='font-bold text-[#101828] mb-2'
              style={{ fontSize: '20px' }}
            >
              {getTemplateName()}
            </h5>

            {/* ⭐ Template Description - Generic hardcoded description */}
            <div className='mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200'>
              <p
                className='text-[#667085] text-sm text-left'
                style={{ fontSize: '13px', lineHeight: '21px' }}
              >
                {getTemplateDescription()}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Right Column: Final Setup */}
      <div className='w-1/3 border rounded-lg p-4 bg-[#F5F8FA] shadow-sm flex flex-col'>
        <h4
          className='font-semibold text-[#3F5265] mb-6 p-2'
          style={{ fontSize: '18px' }}
        >
          Final setup
        </h4>

        {/* Main Content */}
        <div className='flex-grow'>
          <div className='space-y-2 mb-6'>
            <label
              className='text-[#9FA9B2] font-normal'
              style={{ fontSize: '12px' }}
            >
              TIMEZONE*
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className={`block w-full border rounded-lg p-2 ${!timezone ? 'text-gray-400' : ''}`}
            >
              <option value=''>Select Timezone</option>
              <option value='(UTC+05:30) Kolkata'>(UTC+05:30) Kolkata</option>
              <option value='(UTC+00:00) London'>(UTC+00:00) London</option>
              <option value='(UTC-05:00) New York'>(UTC-05:00) New York</option>
              <option value='(UTC+01:00) Paris'>(UTC+01:00) Paris</option>
              <option value='(UTC+02:00) Dubai'>(UTC+02:00) Dubai</option>
              <option value='(UTC+09:00) Tokyo'>(UTC+09:00) Tokyo</option>
              <option value='(UTC-08:00) Los Angeles'>(UTC-08:00) Los Angeles</option>
            </select>
          </div>

          {/* ⭐ Send Options */}
          <div className='space-y-1'>
            <div
              className='flex flex-col gap-1 text-[#3F5265]'
              style={{ fontSize: '14px' }}
            >
              <label className='p-2'>
                <input
                  type='radio'
                  checked={sendOption === 'now'}
                  onChange={() => setSendOption('now')}
                  className='mr-2'
                />
                Send now
              </label>
              <label className='p-2'>
                <input
                  type='radio'
                  checked={sendOption === 'schedule'}
                  onChange={() => setSendOption('schedule')}
                  className='mr-2'
                />
                Schedule
              </label>
            </div>
          </div>

          {/* ⭐ Date and Time - Show only when Schedule is selected */}
          {sendOption === 'schedule' && (
            <div className='mt-4 space-y-3'>
              <div className='flex gap-3'>
                <div className='flex-1'>
                  <label
                    className='text-[#9FA9B2] font-normal'
                    style={{ fontSize: '12px' }}
                  >
                    DATE*
                  </label>
                  <input
                    type='date'
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className='block w-full border rounded-lg p-2 mt-1'
                  />
                </div>
                <div className='flex-1'>
                  <label
                    className='text-[#9FA9B2] font-normal'
                    style={{ fontSize: '12px' }}
                  >
                    TIME*
                  </label>
                  <input
                    type='time'
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className='block w-full border rounded-lg p-2 mt-1'
                  />
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Bottom Buttons */}
        <div className='flex justify-end gap-3 mt-6'>
          <button
            onClick={handleBack}
            className='bg-gray-200 text-gray-700 px-6 py-2 rounded-md font-medium hover:bg-gray-300'
            disabled={isProcessing}
          >
            ← Back
          </button>
          <button
            disabled={!isFormValid() || isProcessing}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${isFormValid() && !isProcessing
              ? 'bg-orange-500 text-white hover:bg-orange-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            onClick={handleAction}
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                  />
                </svg>
                Processing...
              </span>
            ) : (
              sendOption === 'now' ? 'Send Now' : 'Schedule'
            )}
          </button>
        </div>
      </div>

      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default ConfirmSend;